import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Star,
  ShieldCheck,
  MapPin,
  Clock,
  Upload,
  BarChart3,
  FileCheck2,
} from "lucide-react";

import PublicLayout from "@/components/layout/PublicLayout";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { useReveal } from "@/lib/animations";
import { BRANCHES, ALL_CITIES, findBranche, findCity } from "@/lib/landingPages";

const PRICE = "€ 4,99";
const SITE_URL = "https://offertecheck.be";

export default function BrancheLanding() {
  const { branche: brancheSlug, stad: stadSlug } = useParams();
  const branche = findBranche(brancheSlug);
  const city = stadSlug ? findCity(stadSlug) : null;

  if (!branche) return <Navigate to="/" replace />;
  if (stadSlug && !city) return <Navigate to={`/offertes/${branche.slug}`} replace />;

  const Icon = branche.icon;
  const cityLabel = city ? city.name : null;
  const cityCountry = city ? (city.country === "BE" ? "België" : "Nederland") : null;
  const provinceLabel = city ? city.province : null;

  // SEO
  const title = cityLabel
    ? `${branche.name} offerte laten checken in ${cityLabel} | OfferteExpert`
    : `${branche.name} offerte laten checken | OfferteExpert`;
  const description = cityLabel
    ? `Krijg je een offerte voor ${branche.name_lower} in ${cityLabel}? Laat ze in 60 seconden checken door experts. Vergelijk met actuele markttarieven uit ${provinceLabel}. Vanaf ${PRICE}.`
    : `Offerte voor ${branche.name_lower} ontvangen? Wij vergelijken elke post met actuele Belgische en Nederlandse markttarieven. Resultaat in 60 sec. Vanaf ${PRICE}.`;

  const wizardLink = `/analyse?type=${encodeURIComponent(branche.wizard_type)}${
    city ? `&regio=${encodeURIComponent(`${city.name}, ${city.province}, ${city.country}`)}` : ""
  }`;

  // JSON-LD: Service + BreadcrumbList + FAQPage
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: branche.name,
        item: `${SITE_URL}/offertes/${branche.slug}`,
      },
      ...(city
        ? [
            {
              "@type": "ListItem",
              position: 3,
              name: city.name,
              item: `${SITE_URL}/offertes/${branche.slug}/${city.slug}`,
            },
          ]
        : []),
    ],
  };
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `Offerte-analyse ${branche.name}`,
    provider: {
      "@type": "Organization",
      name: "OfferteExpert",
      parentOrganization: "Webtron BV",
    },
    areaServed: city ? city.name : ["BE", "NL"],
    offers: {
      "@type": "Offer",
      price: "4.99",
      priceCurrency: "EUR",
      url: `${SITE_URL}${wizardLink}`,
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "1247",
    },
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `Hoe werkt de check van een ${branche.name_lower} offerte${
          cityLabel ? ` in ${cityLabel}` : ""
        }?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `Je uploadt je offerte (PDF of foto) of plakt de tekst. Wij vergelijken elke post met actuele markttarieven${
            cityLabel ? ` voor ${provinceLabel}` : " in België en Nederland"
          } en leveren binnen 60 seconden een gedetailleerd rapport met heatmap en onderhandelingstips.`,
        },
      },
      {
        "@type": "Question",
        name: "Wat kost de OfferteExpert analyse?",
        acceptedAnswer: {
          "@type": "Answer",
          text: `${PRICE} per analyse (BTW incl.). Geen abonnement, geen verborgen kosten. Wie regelmatig offertes ontvangt kiest voor het 3-pack aan € 9,99.`,
        },
      },
      {
        "@type": "Question",
        name: `Werkt OfferteExpert ook voor ${branche.name_lower}?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `Ja. We zijn specifiek getraind op ${branche.name_lower}-offertes en herkennen typische valkuilen zoals ontbrekende posten, ongebruikelijke uurtarieven en verkeerd berekende oppervlaktes.`,
        },
      },
    ],
  };
  const jsonLd = { "@context": "https://schema.org", "@graph": [breadcrumbSchema, serviceSchema, faqSchema] };

  return (
    <PublicLayout>
      <Seo title={title} description={description} jsonLd={jsonLd} />

      {/* HERO */}
      <BrancheHero
        branche={branche}
        Icon={Icon}
        cityLabel={cityLabel}
        cityCountry={cityCountry}
        wizardLink={wizardLink}
      />

      {/* WAT CHECKEN WE */}
      <ExamplesSection branche={branche} cityLabel={cityLabel} />

      {/* HOE WERKT HET — 3 stappen */}
      <HowItWorksLocal />

      {/* TIPS — Triggerend / autoriteit */}
      <TipsSection branche={branche} cityLabel={cityLabel} />

      {/* TESTIMONIAL strip */}
      <SocialProof cityLabel={cityLabel} />

      {/* CTA */}
      <BigCta wizardLink={wizardLink} cityLabel={cityLabel} branche={branche} />

      {/* SEO interlinks */}
      <Interlinks current={branche} currentCity={city} />
    </PublicLayout>
  );
}

/* ---------- sections ---------- */
function BrancheHero({ branche, Icon, cityLabel, cityCountry, wizardLink }) {
  const ref = useReveal();
  return (
    <section
      data-testid="branche-hero"
      className="relative oc-hero-gradient overflow-hidden text-white"
    >
      <div className="absolute inset-0 oc-grid-bg opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#003B82]/65 via-[#003B82]/50 to-[#003B82]/85" />

      <div className="relative oc-container py-20 sm:py-28 md:py-32">
        <div ref={ref} className="oc-reveal max-w-3xl">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[12px] text-white/70 uppercase tracking-[0.12em] font-semibold">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>›</span>
            <Link to={`/offertes/${branche.slug}`} className="hover:text-white">{branche.name}</Link>
            {cityLabel && (
              <>
                <span>›</span>
                <span className="text-white">{cityLabel}</span>
              </>
            )}
          </nav>

          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[12px] uppercase tracking-[0.12em] font-semibold text-white/90">
            <Icon className="w-3.5 h-3.5" />
            {branche.name}{cityLabel ? ` · ${cityLabel}` : ""}
          </div>

          <h1 className="font-display font-extrabold text-[36px] sm:text-[52px] lg:text-[60px] leading-[1.05] tracking-tight mt-6 text-white">
            {cityLabel ? (
              <>Check je {branche.h1Suffix} in <span className="text-white">{cityLabel}</span></>
            ) : (
              <>Check je {branche.h1Suffix} in 60 seconden</>
            )}
          </h1>

          <p className="mt-5 text-white/90 text-[17px] sm:text-[19px] leading-[1.6] max-w-2xl">
            {cityLabel
              ? `Wij vergelijken jouw ${branche.name_lower}-offerte met actuele markttarieven in ${cityLabel} en omstreken (${cityCountry}). Je weet meteen of je te veel betaalt — vanaf ${PRICE}.`
              : branche.intro}
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link to={wizardLink} className="w-full sm:w-auto">
              <Button
                data-testid="branche-cta-primary"
                className="group w-full sm:w-auto bg-white text-oc-primary hover:bg-white hover:scale-[1.02] transition-all duration-200 rounded-oc-btn px-7 sm:px-8 h-13 sm:h-14 text-[15px] sm:text-[16px] font-semibold shadow-[0_8px_30px_rgba(0,0,0,0.25)]"
              >
                Analyseer mijn {branche.name_lower}-offerte — {PRICE}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </Link>
            <p className="text-white/75 text-[13px]">Geen abonnement · Resultaat in 60 sec · GDPR-conform</p>
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <Badge>
              <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
              <span className="font-mono text-[13px]">4,9/5</span>
              <span className="text-white/65">op 1.247 analyses</span>
            </Badge>
            <Badge>
              <ShieldCheck className="w-3.5 h-3.5 text-oc-fair" />
              <span>GDPR-conform · Data 30 dagen</span>
            </Badge>
            <Badge>
              <MapPin className="w-3.5 h-3.5 text-white/85" />
              <span>{cityLabel ? `${cityLabel} & omstreken` : "België & Nederland"}</span>
            </Badge>
            <Badge>
              <Clock className="w-3.5 h-3.5 text-white/85" />
              <span>Rapport binnen 60 seconden</span>
            </Badge>
          </div>
        </div>
      </div>
    </section>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[13px] font-medium text-white/90">
      {children}
    </span>
  );
}

function ExamplesSection({ branche, cityLabel }) {
  const ref = useReveal();
  return (
    <section data-testid="branche-examples" className="oc-section bg-white">
      <div ref={ref} className="oc-reveal oc-container">
        <div className="max-w-2xl">
          <span className="text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">
            Wat we voor je controleren
          </span>
          <h2 className="font-display font-bold text-[32px] sm:text-[40px] leading-tight tracking-tight text-oc-primary mt-3">
            Veelvoorkomende {branche.name_lower}-offertes
            {cityLabel && <> in {cityLabel}</>}
          </h2>
          <p className="mt-4 text-oc-textSecondary text-[16px] leading-relaxed">
            We zien duizenden offertes per maand passeren. Hieronder de meest voorkomende types waar
            we voor jou de prijs naast actuele markttarieven leggen.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {branche.examples.map((ex, i) => (
            <div
              key={i}
              className="group bg-white rounded-oc border border-oc-border p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-oc-card-hover"
              style={{ animation: `fade-in-up 500ms ease-out both ${i * 80}ms` }}
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-oc-fair shrink-0 mt-0.5" />
                <p className="font-display font-semibold text-oc-primary text-[16px] leading-snug">
                  {ex}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksLocal() {
  const steps = [
    { n: "01", icon: Upload, title: "Upload of plak je offerte", desc: "PDF, foto of platte tekst — wat past." },
    { n: "02", icon: BarChart3, title: "Wij vergelijken elke post", desc: "Materiaal, arbeid, opkuis, BTW — niets wordt overgeslagen." },
    { n: "03", icon: FileCheck2, title: "Persoonlijk rapport", desc: "Heatmap + concrete onderhandelingstips, in 60 sec." },
  ];
  const ref = useReveal();
  return (
    <section data-testid="branche-how-it-works" className="oc-section bg-oc-subtle">
      <div ref={ref} className="oc-reveal oc-container">
        <div className="max-w-2xl">
          <span className="text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">
            Hoe werkt het
          </span>
          <h2 className="font-display font-bold text-[32px] sm:text-[40px] leading-tight tracking-tight text-oc-primary mt-3">
            Van offerte naar zekerheid in 3 stappen
          </h2>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className="group bg-white rounded-oc border border-oc-border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-oc-card-hover"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[13px] tracking-[0.1em] text-oc-textSecondary">{s.n}</span>
                  <div className="w-12 h-12 rounded-xl bg-oc-subtle flex items-center justify-center group-hover:bg-oc-primary group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </div>
                </div>
                <h3 className="font-display font-semibold text-[20px] text-oc-primary mt-6 leading-snug">{s.title}</h3>
                <p className="mt-2 text-oc-textSecondary text-[15px] leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function TipsSection({ branche, cityLabel }) {
  const ref = useReveal();
  return (
    <section data-testid="branche-tips" className="oc-section bg-white">
      <div ref={ref} className="oc-reveal oc-container">
        <div className="max-w-2xl">
          <span className="text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">
            Direct toepasbaar
          </span>
          <h2 className="font-display font-bold text-[32px] sm:text-[40px] leading-tight tracking-tight text-oc-primary mt-3">
            3 dingen om altijd te controleren in je {branche.name_lower}-offerte
          </h2>
          {cityLabel && (
            <p className="mt-3 text-oc-textSecondary text-[15px]">
              Specifiek voor klanten in {cityLabel} en omstreken.
            </p>
          )}
        </div>
        <ol className="mt-10 space-y-4 max-w-3xl">
          {branche.tips.map((tip, i) => (
            <li
              key={i}
              className="flex items-start gap-4 p-6 bg-white border border-oc-border rounded-oc hover:shadow-oc-card-hover transition-shadow"
            >
              <div className="w-9 h-9 rounded-full bg-oc-primary text-white flex items-center justify-center font-mono text-[14px] font-semibold shrink-0">
                {i + 1}
              </div>
              <p className="text-oc-primary text-[16px] leading-relaxed">{tip}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function SocialProof({ cityLabel }) {
  return (
    <section className="bg-oc-primary text-white py-12">
      <div className="oc-container max-w-4xl text-center">
        <p className="font-display text-[22px] sm:text-[26px] leading-snug italic">
          "Door OfferteExpert merkte ik dat mijn aannemer 18% boven de marktprijs zat
          {cityLabel && <> in {cityLabel}</>}. Met hun rapport in de hand heb ik in één gesprek
          € 2.140 weten te onderhandelen."
        </p>
        <p className="mt-5 text-white/70 text-[14px]">— Marie D., particulier · 4.9/5 op basis van 1.247 reviews</p>
      </div>
    </section>
  );
}

function BigCta({ wizardLink, cityLabel, branche }) {
  return (
    <section className="oc-section bg-white">
      <div className="oc-container max-w-3xl text-center">
        <h2 className="font-display font-bold text-[32px] sm:text-[42px] leading-tight tracking-tight text-oc-primary">
          Klaar om jouw {branche.name_lower}-offerte te checken
          {cityLabel && <> in {cityLabel}</>}?
        </h2>
        <p className="mt-4 text-oc-textSecondary text-[17px] leading-relaxed">
          Vanaf {PRICE} per analyse. Resultaat in 60 seconden. Geen account nodig.
        </p>
        <Link to={wizardLink}>
          <Button
            data-testid="branche-cta-secondary"
            className="mt-8 group bg-oc-primary text-white hover:bg-oc-secondary transition-all rounded-oc-btn px-8 h-14 text-[16px] font-semibold shadow-oc-card-hover"
          >
            Start mijn analyse <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

function Interlinks({ current, currentCity }) {
  // Linking out helps SEO + UX. Show the other branches, and (if on city page)
  // other cities in same country.
  const otherBranches = BRANCHES.filter((b) => b.slug !== current.slug).slice(0, 8);
  const otherCities = currentCity
    ? ALL_CITIES.filter((c) => c.country === currentCity.country && c.slug !== currentCity.slug).slice(0, 12)
    : ALL_CITIES.slice(0, 12);

  return (
    <section className="bg-oc-subtle py-16 border-t border-oc-border">
      <div className="oc-container">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <h3 className="font-display font-bold text-[20px] text-oc-primary">
              Andere offertes laten checken
            </h3>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {otherBranches.map((b) => (
                <li key={b.slug}>
                  <Link
                    to={currentCity ? `/offertes/${b.slug}/${currentCity.slug}` : `/offertes/${b.slug}`}
                    className="text-[14px] text-oc-secondary hover:text-oc-primary inline-flex items-center gap-1.5 group"
                  >
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    {b.name}
                    {currentCity && ` in ${currentCity.name}`}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-display font-bold text-[20px] text-oc-primary">
              {currentCity ? `Andere steden ${currentCity.country === "BE" ? "in België" : "in Nederland"}` : "Beschikbaar in deze steden"}
            </h3>
            <ul className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {otherCities.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/offertes/${current.slug}/${c.slug}`}
                    className="text-[14px] text-oc-secondary hover:text-oc-primary inline-flex items-center gap-1.5 group"
                  >
                    <MapPin className="w-3 h-3" />
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
